import {
  Box,
  Text,
  Button,
} from '@chakra-ui/react';
import {
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions, GridReadyEvent, ValueFormatterParams } from 'ag-grid-community';
import { agGridIconsConfig } from '@/explore/ffl-detail/utils.mjs';
import { UserListInterface } from '@/model/userList.mjs';
import AppConfig from '@/utils/app-config.mjs';
import { client } from '@/api/backoffice-api';
import { useDispatch } from 'react-redux';
import { downloadPreferredDealersListForExcel, setIsExportPreferredDealersButtonDisabled, setPreferredDealersBEAPICall, setPreferredDealersMetaData } from '../ducks/slice';
import { getIsLoadingPreferredDealersExcelGeneration, getIsExportPreferredDealersButtonDisabled } from '../ducks/selectors';
import { formatMillisecondsToDate } from '../utils';
import { PreferredDealersMetaData } from '../types';
import logger from '@/utils/logger.mjs';
import { getIsGlobalAdminRole, getIsImpersonating, getIsPreferredReportsRole, getIsStoreAdmin } from '@/redux/app-selectors';
import { Link } from 'react-router-dom';
  
export default function PreferredDealersReport() {
  const rowsPerPage = 30;
  const [metaData, setMetaData] = useState<PreferredDealersMetaData>();
  const gridRef = useRef<AgGridReact<UserListInterface>>(null);
  const gridStyle = useMemo(() => ({ width: '100%' }), []);
  const dispatch = useDispatch();
  const isLoadingPreferredDealersExcelGeneration = getIsLoadingPreferredDealersExcelGeneration();
  const isExportPreferredDealersButtonDisabled = getIsExportPreferredDealersButtonDisabled();
  const isStoreAdmin = getIsStoreAdmin();
  const isImpersonating = getIsImpersonating();
  const isGlobalAdminRole = getIsGlobalAdminRole();
  const isPreferredReportsRole = getIsPreferredReportsRole();

  const sortingData = (a: string | number, b: string | number) => {
    const valueA = typeof a === 'string' ? a.toLowerCase() : a;
    const valueB = typeof b === 'string' ? b.toLowerCase() : b;
    return valueA == valueB ? 0 : valueA > valueB ? 1 : -1;
  };

  const licenseIdCellRenderer = ({data}: any) => {
    return !data?.licenses[0]?.licenseKey ? 
        <Box className="transfer-transfer-type-wrapper">
              <><img src="https://www.ag-grid.com/example-assets/loading.gif" />&nbsp; Loading</>
          </Box> : <Link to={`${window.location.origin}/dealers-search/${data?.dealerId}`} target='_blank'>
                      <Text color='#0065FF'>{data?.licenses[0]?.licenseKey}</Text>
                  </Link>;
  };
  const websiteRenderer = ({data}:any) => {
    if(data?.website){
      const url = data?.website.startsWith('http') ? data?.website : `https://${data?.website}`;
      let link = data?.website
      const formattedLink = link.length > 30 ? (link.slice(0,30) + '...'): link
      return <Link to={url} target='_blank'>
        <Text color='#0065FF'>{formattedLink}</Text>
      </Link>
    } else return '-'
  }
  const facebookUrlRenderer = ({data}:any) => {
    if(data?.socialProfiles?.facebook){
      const url = data?.socialProfiles?.facebook.startsWith('http') ? data?.socialProfiles?.facebook : `https://${data?.socialProfiles?.facebook}`;
      const formattedLink = url.length > 30 ? (url.slice(0,30) + '...'): url
      return <Link to={url} target='_blank'>
        <Text color='#0065FF'>{formattedLink}</Text>
      </Link>
    } else return '-'
  }

  const columnDefs: ColDef[] = [
    {
      field: 'licenses',
      headerName: 'License ID',
      width: 220,
      comparator: sortingData,
      sortable: false,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        const licenseKey = data?.licenses[0]?.licenseKey || null;
        return licenseKey
        ? licenseKey
        : null;
      },
      cellRenderer: licenseIdCellRenderer,
    },
    {
      field: 'primaryBusinessName',
      headerName: 'Business Name',
      width: 350,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const businessName = data?.primaryBusinessName || null;
        return businessName
          ? businessName
          : "-";
      }
    },
    {
      field: 'totalTransferCounts',
      headerName: 'Total Transfers',
      width: 180,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const transferCounts = data?.totalTransferCounts || null;
        return transferCounts
          ? transferCounts
          : "0";
      }
    },
    {
      field: 'isCommercial',
      headerName: 'RBDI',
      width: 150,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const RBDI = typeof data?.isCommercial === 'boolean' ? (data?.isCommercial ? "Commercial" : "Residential") : "-";
        return RBDI;
      }
    },
    {
      field: 'isClaimed',
      headerName: 'Is Claimed',
      width: 180,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const isClaimed = data?.isClaimed || null;
        return isClaimed
          ? "Yes"
          : "No";
      }
    },
    {
      field: 'contact',
      headerName: 'Primary Contact',
      width: 250,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const primaryContact = data?.contact?.value || null;
        return primaryContact
          ? primaryContact
          : "-";
      }
    },
    {
      field: 'phoneNumbers',
      headerName: 'Primary Phone',
      width: 250,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const primaryPhone = (data?.phoneNumbers && data?.phoneNumbers[0]) || null;
        return primaryPhone
          ? primaryPhone
          : "-";
      }
    },
    {
      field: 'consolidatedRatings',
      headerName: 'Rating',
      width: 150,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const ratings = data?.consolidatedRatings?.ratings || null;
        return ratings
          ? ratings
          : "-";
      }
    },
    {
      field: 'consolidatedRatings',
      headerName: 'Reviews',
      width: 150,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const reviews = data?.consolidatedRatings?.reviews || null;
        return reviews
          ? reviews
          : "-";
      }
    },
    {
      field: 'licenses',
      headerName: 'Expiry Date',
      width: 200,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const expiryDate = data?.licenses[0]?.expiryDate || null;
        return expiryDate
          ? formatMillisecondsToDate(expiryDate)
          : null;
      }
    },
    {
        field: 'licenseTypes',
        headerName: 'Has Valid License',
        width: 200,sortable: false,
        comparator: sortingData,
        valueFormatter: function ({ data }: ValueFormatterParams) {
          if(!data) return "";
          const validLicense = data?.licenseTypes[0]?.hasValidCertificate || null;
          return validLicense
            ? "Yes"
            : "No";
        }
    },
    {
        field: 'isSotDealer',
        headerName: 'Is SOT',
      width: 120,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const isSOT = data?.isSotDealer || null;
        return isSOT
        ? "Yes"
        : "No";
      }
    },
    {
      field: 'sot',
      headerName: 'Has SOT Tax Stamp',
      width: 250,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
      if(!data) return "";
      const employeeIdentificationNumber = data?.sot?.employeeIdentificationNumber || null;
      return employeeIdentificationNumber
      ? "Yes"
      : "No";
      }
    },
    {
      field: 'optedInAllStores',
      headerName: 'Opt-In Status',
      width: 250,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
          if(!data) return "";
          const OptInStatus = data?.optedInAllStores ? "Opted In All Stores" : data?.optedInForStore ? "Opted In This Store" : "";
          return OptInStatus;
        }
    },
    {
      field: 'optedOutAllStores',
      headerName: 'Opt-Out Status',
      width: 250,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
          const OptInStatus = data?.optedOutAllStores ? "Opted Out All Stores" : data?.optedOutForStore ? "Opted Out This Store" : "";
          return OptInStatus;
        }
    },
    {
      field: 'emails',
      headerName: 'Transfer Emails',
      width: 260,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        return data?.emails?.join(', ') || '-';
      }
    },
    {
      field: 'firearms',
      headerName: 'Handgun Fee',
      width: 200,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const handgunFee = data?.firearms?.handGun || null;
        return handgunFee ? handgunFee : '-';
      }
    },
    {
      field: 'firearms',
      headerName: 'Long Gun Fee',
      width: 200,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const longGunFee = data?.firearms?.longGun || null;
        return longGunFee ? longGunFee : '-';
      }
    },
    {
      field: 'firearms',
      headerName: 'Other Firearm Fee',
      width: 250,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const otherFirearmFee = data?.firearms?.other || null;
        return otherFirearmFee ? otherFirearmFee : '-';
      }
    },
    {
      field: 'firearms',
      headerName: 'NFA Fee',
      width: 200,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const NFAFee = data?.firearms?.nfa || null;
        return NFAFee ? NFAFee : '-';
      }
    },
    {
      field: 'socialProfiles',
      headerName: 'Facebook',
      width: 250,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const facebookUrl = data?.socialProfiles?.facebook || null;
        return facebookUrl ? facebookUrl : '-';
      },
      cellRenderer: facebookUrlRenderer
    },
    {
      field: 'website',
      headerName: 'Website',
      width: 250,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const website = data?.website || null;
        return website ? website : '-';
      },
      cellRenderer: websiteRenderer
    },
    {
      field: 'businessTypes',
      headerName: 'Business category',
      width: 300,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const businessTypes = data?.businessTypes?.join(', ') || '-'
        return businessTypes
      }
    },
    {
      field: 'primaryBusiness',
      headerName: 'Primary Business Indicator',
      width: 300,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        return data?.primaryBusiness ? 'Yes' : 'No'
      }
    },
    {
      field: 'cfd',
      headerName: 'CFD',
      width: 300,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        return data?.cfd ? data?.cfd : '-'
      }
    },
    {
      field: 'isRangeDealer',
      headerName: 'Shooting Range',
      width: 300,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        return data?.isRangeDealer ? 'Yes' : 'No'
      }
    },
    {
      field: 'isBigBoxRetailer',
      headerName: 'Is Big Box Retailer',
      width: 300,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        return data?.isBigBoxRetailer ? 'Yes' : 'No'
      }
    },
    {
      field: 'openOnWeekends',
      headerName: 'Open on Weekends',
      width: 300,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        return data?.openOnWeekends ? 'Yes' : 'No'
      }
    },
    {
      field: 'licenseTypes',
      headerName: 'FFL License Type',
      width: 300,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const licenseType: Array<String> = [];
        data?.licenseTypes?.map((record: any) => licenseType.push(record?.type))
        const licenseTypes = licenseType.join(", ")
        return licenseTypes
      }
    },
    {
      field: 'services',
      headerName: 'Services',
      width: 300,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        if(!data) return "";
        const services = data?.services?.join(', ') || "-"
        return services
      }
    },
    {
      field: 'blackListedStoresCount',
      headerName: 'BlackListed Stores Count',
      width: 300,
      sortable: false,
      comparator: sortingData,
      valueFormatter: function ({ data }: ValueFormatterParams) {
        console.log({blackListedStoresCount: data?.blackListedStoresCount})
        if(!data) return "";
        const services = data?.blackListedStoresCount || "0"
        return services
      }
    },
    {
        field: 'isTemporarilyClosed',
        headerName: 'Operational status',
        width: 300,
        sortable: false,
        comparator: sortingData,
        valueFormatter: function ({ data }: ValueFormatterParams) {
          if(!data) return "";
          const operationalStatus =  data?.isPermanentlyClosed ? "Permanently Closed" : data?.isTemporarilyClosed ? "Temporarily Closed" : "Operational"
          return operationalStatus
        }
      },
    {
        field: 'address',
        headerName: 'Premise Address',
        width: 300,
        sortable: false,
        comparator: sortingData,
        valueFormatter: function ({ data }: ValueFormatterParams) {
          if(!data) return "";
          return `${data?.address?.street}, ${data?.address?.city}, ${data?.address?.state}, ${data?.address?.zipCode}`
        }
      },
  ];
  
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
    }),
    []
  );

  const onBtShowNoRows = useCallback(() => {
    gridRef.current?.api.showNoRowsOverlay();
  }, []);

  function onBtHide() {
    gridRef.current?.api.hideOverlay();
  }

  const onGridReady = useCallback(
    (gridEvent: GridReadyEvent<any>) => {
      const dataSource = {
        rowCount: undefined,
        getRows(params: any): void {
          const pageNumber = params.startRow / rowsPerPage + 1;
          onBtHide();
          client.get(`${AppConfig.fflExpressServiceApiURL}/dealers/search?only_preferred_dealer=true&page_size=${rowsPerPage}&page_number=${pageNumber}&include_opt_out_dealers=true&include_permanently_closed_dealers=true&include_big_box_retailer=true`)
          .then((result) => {
              let lastRow = result.data.metadata.hasMorePages
                ? -1
                : result.data.metadata.totalRecords
              if (
                result.data.data.length === 0 &&
                gridEvent.api.getDisplayedRowCount() <= 1
              ) {
                onBtShowNoRows();
              }
              params.successCallback(result.data.data, lastRow);
              setMetaData(result.data.metadata);
              if(result?.data?.metadata?.totalRecords === 0) dispatch(setIsExportPreferredDealersButtonDisabled(true))
            })
            .catch((error) => {
              logger.error('Error while fetching rows for ag-Grid', error);
              params.failCallback();
            })
        }
      };
      gridEvent.api.setGridOption('datasource', dataSource);
      gridEvent.api.autoSizeColumns(columnDefs)
    },
    []
  );

  const gridOptions: GridOptions<any> = {
    rowModelType: 'infinite',
    infiniteInitialRowCount: 1,

    maxConcurrentDatasourceRequests: 1,
    cacheBlockSize: rowsPerPage,
    
    // generate a unique ID for each row based on the abuse ID
    getRowId: (params) => {
      return params.data.id || '';
    },
    
    // column definitions
    columnDefs,
    defaultColDef,

    // row options
    animateRows: true,
    suppressRowClickSelection: true,

    // cell detail options
    suppressCellFocus: true,

    onGridReady,

    // misc options
    debug: false,
    icons: agGridIconsConfig,
    overlayNoRowsTemplate:
      '<div class="not-found-box"><img src="/images/ui/not-found-icon.png"></img><p class="not-found-box__title">No Preferred Dealers Found</p><p class="not-found-box__message">Try adjusting your search or filter to find what you’re looking for</p></div>'
  };

  function downloadReport(){
      console.log({isGlobalAdminRole, isImpersonating, isStoreAdmin});
      if(isGlobalAdminRole && isImpersonating && isStoreAdmin){
        console.log("Set setPreferredDealersBEAPICall");
        dispatch(setPreferredDealersBEAPICall(true));
      } else {
        dispatch(setPreferredDealersBEAPICall(false));
        dispatch(setPreferredDealersMetaData(metaData as PreferredDealersMetaData))
      }
      dispatch(downloadPreferredDealersListForExcel(gridRef as any))
  }

  return (
    <Box className={'Queue'}>
      <Box className="transfer-header">
      <Text as='b' fontSize='lg'>Total Preferred Dealers: {metaData?.totalRecords || ""}</Text>
      <div className="transfer-header__right">
          {(isStoreAdmin || isPreferredReportsRole) && <Box className="transfer-filters" onClick={downloadReport}>
              <Button
                  colorScheme="blue"
                  background="#0C1E46"
                  padding="8px 20px"
                  loadingText={"Exporting"} 
                  isLoading={isLoadingPreferredDealersExcelGeneration} 
                  isDisabled={isExportPreferredDealersButtonDisabled}
              >
                  Export as Excel
              </Button>
          </Box>}
      </div>
    </Box>
      <div style={gridStyle} className='grid-height'>
        <AgGridReact
          className={'ag-theme-alpine'}
          ref={gridRef}
          gridOptions={gridOptions}
        />
      </div>
    </Box>
  );
}
